import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiClient, API_ENDPOINTS } from '../config/api';

interface AuthUser {
  id: number;
  username: string;
  role: string;
}

interface SubjectOption {
  id: number;
  label: string;
  description?: string | null;
  sortOrder: number;
}

interface Conversation {
  id: number;
  subjectOptionId?: number | null;
  subjectLabel: string;
  subjectFreeText?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  lastMessageAt?: string | null;
  lastMessagePreview?: string | null;
}

interface ContactMessage {
  id: number;
  role: 'user' | 'assistant';
  messageText: string;
  feedbackValue?: 'up' | 'unclear' | null;
  feedbackAt?: string | null;
  createdAt: string;
}

interface ContactSupportProps {
  currentUser: AuthUser | null;
}

const extractErrorMessage = (err: unknown, fallback: string) => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error?.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '未記録';
  }
  const normalized = /z$|[+-]\d{2}:\d{2}$/i.test(value) ? value : `${value}Z`;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return '未記録';
  }
  return parsed.toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
};

const ContactSupport: React.FC<ContactSupportProps> = ({ currentUser }) => {
  const [subjectOptions, setSubjectOptions] = useState<SubjectOption[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [selectedSubjectOptionId, setSelectedSubjectOptionId] = useState<string>('');
  const [inputValue, setInputValue] = useState('');
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedSubject = useMemo(() => {
    const numericId = Number(selectedSubjectOptionId);
    return subjectOptions.find((item) => item.id === numericId) || null;
  }, [selectedSubjectOptionId, subjectOptions]);

  const activeConversation = useMemo(
    () => conversations.find((item) => item.id === activeConversationId) || null,
    [activeConversationId, conversations]
  );

  const displaySubjectLabel = activeConversation?.subjectLabel || selectedSubject?.label || '';

  const loadSubjectOptions = useCallback(async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.contactSubjectOptions);
      setSubjectOptions(response.data.subjectOptions || []);
    } catch (err) {
      setError(extractErrorMessage(err, '件名候補の取得に失敗しました。'));
    }
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.contactConversations);
      setConversations(response.data.conversations || []);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'お問い合わせ履歴の取得に失敗しました。'));
    }
  }, []);

  const loadConversationDetail = useCallback(async (conversationId: number) => {
    setIsLoadingMessages(true);
    try {
      const response = await apiClient.get(API_ENDPOINTS.contactConversationDetail(conversationId));
      setMessages(response.data.messages || []);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'お問い合わせ内容の取得に失敗しました。'));
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadSubjectOptions();
    loadConversations();
  }, [loadConversations, loadSubjectOptions]);

  useEffect(() => {
    if (activeConversationId) {
      loadConversationDetail(activeConversationId);
    } else {
      setMessages([]);
    }
  }, [activeConversationId, loadConversationDetail]);

  const handleCreateConversation = async (subjectOptionId: string) => {
    if (!subjectOptionId) {
      setError('件名を選択してください。');
      return null;
    }

    const numericId = Number(subjectOptionId);
    const subjectOption = subjectOptions.find((item) => item.id === numericId);

    setIsCreatingConversation(true);
    try {
      const response = await apiClient.post(API_ENDPOINTS.contactConversations, {
        subjectOptionId: numericId,
        subjectFreeText: '',
      });
      const conversation = response.data.conversation;
      setActiveConversationId(conversation.id);
      setConversations((prev) => {
        const nextConversation = {
          ...conversation,
          subjectLabel: subjectOption?.label || conversation.subjectLabel,
        };
        return [nextConversation, ...prev.filter((item) => item.id !== nextConversation.id)];
      });
      if (subjectOption) {
        conversation.subjectLabel = subjectOption.label;
      }
      setError(null);
      return conversation;
    } catch (err) {
      setError(extractErrorMessage(err, 'お問い合わせの作成に失敗しました。'));
      return null;
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const handleSubjectChange = (value: string) => {
    setSelectedSubjectOptionId(value);
    setActiveConversationId(null);
    setMessages([]);
    setError(null);
  };

  const handleSendMessage = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      return;
    }
    if (!activeConversationId && !selectedSubjectOptionId) {
      setError('件名を選択してください。');
      return;
    }

    setIsSending(true);
    try {
      let conversationId = activeConversationId;
      if (!conversationId) {
        const createdConversation = await handleCreateConversation(selectedSubjectOptionId);
        conversationId = createdConversation?.id || null;
      }
      if (!conversationId) {
        return;
      }

      const response = await apiClient.post(
        API_ENDPOINTS.contactConversationMessages(conversationId),
        { message: trimmed },
      );
      setMessages((prev) => [
        ...prev,
        response.data.userMessage,
        response.data.assistantMessage,
      ]);
      setInputValue('');
      await loadConversations();
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'お問い合わせ送信に失敗しました。'));
    } finally {
      setIsSending(false);
    }
  };

  const handleFeedback = async (messageId: number, feedbackValue: 'up' | 'unclear') => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.contactMessageFeedback(messageId),
        { feedbackValue },
      );
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                feedbackValue: response.data.contactMessage.feedbackValue,
                feedbackAt: response.data.contactMessage.feedbackAt,
              }
            : message
        )
      );
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, '評価の保存に失敗しました。'));
    }
  };

  return (
    <div className="contact-support-page">
      <section className="login-activity-hero">
        <div>
          <span className="login-activity-eyebrow">Support</span>
          <h2>お問い合わせ / ご要望</h2>
          <p>{currentUser?.username || '利用者'} としてログイン中です。お問い合わせ内容とAI回答は記録されます。</p>
        </div>
      </section>

      {error && <div className="error-message"><span>{error}</span></div>}

      <section className="contact-support-layout">
        <aside className="contact-support-sidebar">
          <div className="login-activity-panel">
            <div className="login-activity-panel-header">
              <div>
                <h3>件名</h3>
                <p>ご質問に近い件名を選択ください。</p>
              </div>
            </div>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <select
                className="contact-subject-select"
                value={selectedSubjectOptionId}
                onChange={(e) => void handleSubjectChange(e.target.value)}
                disabled={isCreatingConversation}
                style={{ padding: '0.375rem 0.45rem' }}
              >
                <option value="" className="contact-subject-placeholder">件名を選択してください</option>
                {subjectOptions.map((option) => (
                  <option key={option.id} value={option.id} className="contact-subject-option">
                    {option.label}
                  </option>
                ))}
              </select>

              {selectedSubject?.description && (
                <div style={{ fontSize: 13, color: '#4b5563' }}>{selectedSubject.description}</div>
              )}
              {isCreatingConversation && (
                <div style={{ fontSize: 13, color: '#6b7280' }}>問い合わせを開始しています...</div>
              )}
            </div>
          </div>
        </aside>

        <section className="contact-support-main">
          <div className="login-activity-panel">
            <div className="login-activity-panel-header">
              <div>
                <h3>お問い合わせ内容</h3>
                {!activeConversationId && !selectedSubjectOptionId && <p>左側で問い合わせを開始してください。</p>}
              </div>
            </div>

            <div className="contact-support-messages">
              {!activeConversationId && !selectedSubjectOptionId && <p>問い合わせ未選択</p>}
              {isLoadingMessages && <p>読み込み中...</p>}
              {messages.map((message) => (
                <div key={message.id} className={`contact-message ${message.role}`}>
                  <div className="contact-message-bubble">
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{message.messageText}</p>
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {formatDateTime(message.createdAt)}
                  </div>
                  {message.role === 'assistant' && (
                    <div className="contact-message-feedback">
                      {message.feedbackValue ? (
                        <span>
                          {message.feedbackValue === 'up' ? '👍' : '😩'}
                        </span>
                      ) : (
                        <>
                          <button onClick={() => handleFeedback(message.id, 'up')} aria-label="分かりやすい">
                            👍
                          </button>
                          <button onClick={() => handleFeedback(message.id, 'unclear')} aria-label="よくわからない">
                            😩
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="contact-support-composer">
              {displaySubjectLabel && (
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>
                  件名：{displaySubjectLabel}
                </div>
              )}
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="お問い合わせ内容を入力してください"
                rows={4}
                disabled={(!activeConversationId && !selectedSubjectOptionId) || isSending || isCreatingConversation}
                style={{ padding: '0.45rem 0.5rem' }}
              />
              <button
                onClick={handleSendMessage}
                disabled={(!activeConversationId && !selectedSubjectOptionId) || isSending || isCreatingConversation}
              >
                {isSending ? '送信中...' : '送信'}
              </button>
            </div>
          </div>
        </section>
      </section>
    </div>
  );
};

export default ContactSupport;
